/**
 * TimelineTaskBookmarkLayer Component
 *
 * Renders expandable bookmarks for expanded crop rows (tasks).
 * Bookmarks extend below task rectangles with labels, durations, and notes.
 * Position: Below phase bookmarks when viewStrategy is 'both'.
 */

import React, { useCallback } from 'react'
import {
  useTimelineContextDispatch,
  setHoveredTaskKey as setHoveredTaskKeyAction,
  setExpandedIds as setExpandedIdsAction,
} from '../../context/timeline-context'
import { useTimelineStaticContext } from '../../context/timeline-static-context'
import { useTimelineInteractionContext } from '../../context/timeline-interaction-context'
import { useOrgHoverContext } from '~/common/context/organization/org-context'
import { useTimelineStyles } from '../../../hooks/use-timeline-styles'
import { Bookmark } from '../content/bookmark'
import { calculateTaskBookmarkWidth } from '../../../utils/bookmark-utils'
import {
  EXPANDED_ROW_HEIGHT,
  EXPANDED_ROW_HEIGHT_GAP,
  VERTICAL_DAY_TRACKER_SPACE,
  TASK_BOOKMARK_HEIGHT,
} from '../../timeline-constants'

// Stable style object — defined once at module level, never reallocated
const ISOLATION_STYLE = { isolation: 'isolate' }

/**
 * TimelineTaskBookmarkLayer - Expandable bookmarks for expanded rows (tasks)
 *
 * Only renders for expanded crops. Bookmarks are sorted so active/hovered
 * bookmarks render on top.
 *
 * @param {Object} props
 * @param {Array} props.crops - Transformed crop data
 * @param {boolean} props.forceAllExpand - Force all crops expanded
 */
export const TimelineTaskBookmarkLayer = React.memo(({ crops, forceAllExpand, interactive, onPanStart, onRemoveTask }) => {
  const {
    expandedIds,
    activeTaskKey,
    weekWidth,
    getRowY,
    viewStrategy,
  } = useTimelineStaticContext()
  const { hoveredTaskKey } = useTimelineInteractionContext()

  const dispatch = useTimelineContextDispatch()
  const { hoveredOrg, hoveredTask } = useOrgHoverContext()

  const { getTaskStyle } = useTimelineStyles()

  // Stable dispatch handlers — wrapped in useCallback so identity is stable across renders
  const handleHoverTask = useCallback((isHovering, taskKey) => {
    dispatch(setHoveredTaskKeyAction({ hoveredTaskKey: isHovering ? taskKey : null }))
  }, [dispatch])

  const handleClickTask = useCallback((crop) => () => {
    if (interactive || crop.interactive) return
    const newExpandedIds = expandedIds.filter(i => i !== crop.id)
    dispatch(setExpandedIdsAction({ expandedIds: newExpandedIds }))
  }, [interactive, expandedIds, dispatch])

  return (
    <>
      {crops.map((crop) => {
        const isExpanded = forceAllExpand || expandedIds.includes(crop.id)
        if (!isExpanded) return null  // Only render bookmarks for expanded crops

        const rowY = getRowY(crop.id) + VERTICAL_DAY_TRACKER_SPACE * 8

        // Calculate row height: double when showing both phases and tasks
        const baseRowH = EXPANDED_ROW_HEIGHT
        const rowH = viewStrategy === 'both' && !crop.isOrgTaskRow ? baseRowH * 2 : baseRowH

        const barH = EXPANDED_ROW_HEIGHT - EXPANDED_ROW_HEIGHT_GAP

        // Calculate sub-row height when both phases and tasks shown
        const subRowH = viewStrategy === 'both' && !crop.isOrgTaskRow ? rowH / 2 : rowH

        // Position in bottom half when both, otherwise center in full row
        const y = viewStrategy === 'both' && !crop.isOrgTaskRow
          ? rowY + subRowH + (subRowH / 2) - (barH / 2)
          : rowY + (rowH / 2) - (barH / 2)

        const isParentOrgHovered = crop.organization_id === hoveredOrg?.id
        const cropInteractive = interactive || crop.interactive

        return (
          <g key={`task-bookmarks-${crop.id}`} style={ISOLATION_STYLE}>
            {crop.tasks
              ?.map((task, tidx) => {
                const s = getTaskStyle(task.type)
                const x = task.start * weekWidth
                const anchorWidth = task.len * weekWidth
                const taskKey = task.id  // Use stable task UUID
                const duration = Math.round(task.len * 7)
                const label = task.label || s.label
                const bookmarkWidth = calculateTaskBookmarkWidth(label, duration, task.label, anchorWidth)
                const isTaskHovered = task.id === hoveredTask

                return {
                  task,
                  tidx,
                  s,
                  x,
                  anchorWidth,
                  taskKey,
                  duration,
                  label,
                  bookmarkWidth,
                  y,
                  notes: task.notes,
                  isTaskHovered,
                  readOnly: task.readOnly ?? false,
                  isMultiSubmit: task.single_completion === false,
                  isDeletable: cropInteractive && !(task.readOnly ?? false) && task.state !== 'completed',
                }
              })
              .sort((a, b) => {
                // Non-readOnly (preview) always renders on top of readOnly (existing) — highest priority
                if (!a.readOnly && b.readOnly) return 1
                if (a.readOnly && !b.readOnly) return -1
                // Within same readOnly group, render active/hovered bookmark on top
                const aIsHovered = a.taskKey === activeTaskKey || a.taskKey === hoveredTaskKey || a.isTaskHovered
                const bIsHovered = b.taskKey === activeTaskKey || b.taskKey === hoveredTaskKey || b.isTaskHovered
                if (aIsHovered) return 1
                if (bIsHovered) return -1
                return 0
              })
              .map(({ task, tidx, taskKey, x, anchorWidth, bookmarkWidth, y, s, duration, isTaskHovered, isMultiSubmit, isDeletable }) => (
                <Bookmark
                  key={taskKey}
                  x={x}
                  y={y}
                  anchorWidth={anchorWidth}
                  bookmarkWidth={bookmarkWidth}
                  totalHeight={TASK_BOOKMARK_HEIGHT}
                  style={s}
                  duration={duration}
                  isActive={taskKey === activeTaskKey}
                  isHovered={taskKey === hoveredTaskKey}
                  isParentOrgHovered={isParentOrgHovered || isTaskHovered} // couple the parent org signal
                  onHover={(key) => handleHoverTask(key !== null, taskKey)}
                  onClick={handleClickTask(crop)}
                  onMouseDown={cropInteractive
                    ? (e) => { e.stopPropagation(); e.preventDefault(); onPanStart(tidx, e.clientX) }
                    : undefined
                  }
                  itemKey={taskKey}
                  isMultiSubmit={isMultiSubmit}
                  onDelete={isDeletable && onRemoveTask ? () => onRemoveTask(task.id) : undefined}
                />
              ))}
          </g>
        )
      })}
    </>
  )
})
